import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Building2, Plus, Loader2, ExternalLink, Mail, Trash2, MessageSquare,
  Linkedin, Twitter, Instagram, Facebook, Github, Youtube, Shield,
  Upload, Zap, FileDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  new:            { label: 'New',            color: '#00F0FF', bg: 'bg-[#00F0FF]/10',  border: 'border-[#00F0FF]/30' },
  contacted:      { label: 'Contacted',      color: '#00F0FF', bg: 'bg-[#00F0FF]/10',  border: 'border-[#00F0FF]/30' },
  follow_up_1:    { label: 'Follow-up 1',    color: '#00FF94', bg: 'bg-[#00FF94]/10',  border: 'border-[#00FF94]/30' },
  follow_up_2:    { label: 'Follow-up 2',    color: '#FF6B35', bg: 'bg-[#FF6B35]/10',  border: 'border-[#FF6B35]/30' },
  replied:        { label: 'Replied',        color: '#00FF94', bg: 'bg-[#00FF94]/10',  border: 'border-[#00FF94]/30' },
  converted:      { label: 'Converted',      color: '#00F0FF', bg: 'bg-[#00F0FF]/10',  border: 'border-[#00F0FF]/30' },
  not_interested: { label: 'Not interested', color: '#888888', bg: 'bg-neutral-800/50', border: 'border-white/10' },
};
const STATUS_ORDER = ['all', 'new', 'contacted', 'follow_up_1', 'follow_up_2', 'replied', 'converted', 'not_interested'];

const SOCIAL_ICON = {
  linkedin: Linkedin, twitter: Twitter, instagram: Instagram,
  facebook: Facebook, github: Github, youtube: Youtube,
};

const DEFAULT_EMAIL_TEMPLATE = (name, company) => (
  `Hi ${name || 'there'},\n\n` +
  `I'm Jacob from theailedger.ai — a new marketplace for creators and companies selling AI products (prompts, tools, agents, models).\n\n` +
  `I came across ${company || 'your work'} and thought it might be a strong fit for our Verified Partner program. Highlights:\n` +
  `• 0% platform fee for the first 100 creators (first 6 months)\n` +
  `• Your own Stripe Connect Standard account — keep full control of subscribers\n` +
  `• Homepage feature + dedicated partner profile page at launch\n\n` +
  `Would you be open to a quick 15 min to explore?\n\n` +
  `Jacob Colson\ntheailedger.ai`
);

// Pull a short, sensible "company name" out of the (often verbose) HTML
// page title so we don't end up with email subjects like
//   "Stripe | Financial Infrastructure to Grow Your Revenue × ...".
// Strategy: split on common title delimiters, take the first segment,
// hard cap at 40 chars.
const cleanCompanyName = (raw) => {
  if (!raw) return 'Quick question';
  const first = String(raw).split(/[|–—\-:]/)[0].trim();
  return first.length > 40 ? `${first.slice(0, 37)}…` : first;
};

const DEFAULT_SUBJECT = (company) => (
  `${cleanCompanyName(company)} × theailedger.ai — 0% fee Founding Partner invite`
);


export default function AdminProspects() {
  const [prospects, setProspects] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState('');
  const [newNote, setNewNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [importingCsv, setImportingCsv] = useState(false);
  const [importingPh, setImportingPh] = useState(false);
  const [phTopic, setPhTopic] = useState('artificial-intelligence');
  const [selected, setSelected] = useState(null); // prospect object or null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/prospects`, {
        params: { status: statusFilter, limit: 200 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setProspects(res.data.prospects || []);
      setCounts(res.data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load prospects');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const createProspect = async (e) => {
    e.preventDefault();
    if (!newUrl.trim()) return;
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/prospects`,
        { url: newUrl.trim(), note: newNote.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Prospect added');
      setNewUrl(''); setNewNote('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add prospect');
    } finally {
      setCreating(false);
    }
  };

  // ---------- Bulk imports ----------
  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingCsv(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API_URL}/api/admin/prospects/import/csv`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
      );
      const { inserted_count, skipped_count, skipped } = res.data;
      toast.success(`Imported ${inserted_count} prospects` + (skipped_count ? `, ${skipped_count} skipped` : ''));
      (skipped || []).slice(0, 3).forEach((s) => toast.error(`Row ${s.row}: ${s.reason}`));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV import failed');
    } finally {
      setImportingCsv(false);
      e.target.value = '';
    }
  };

  const importFromProductHunt = async () => {
    if (!phTopic.trim()) return;
    setImportingPh(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/prospects/import/product-hunt`,
        { topic: phTopic.trim(), limit: 30 },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { inserted_count, skipped_count } = res.data;
      toast.success(`Imported ${inserted_count} makers from Product Hunt` + (skipped_count ? `, ${skipped_count} skipped` : ''));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Product Hunt import failed');
    } finally {
      setImportingPh(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-prospects-page">
        <header className="mb-2">
          <h1 className="text-3xl md:text-4xl font-black mb-2 flex items-center gap-3">
            <Building2 className="w-7 h-7 text-[#00F0FF]" />
            Prospect Triage Board
          </h1>
          <p className="text-neutral-400 max-w-2xl text-sm">
            Curated, manual outreach for high-value partners. Paste a company URL,
            we enrich the public-page metadata, you send a personalised email from
            your own mailbox. Zero automated sending — fully compliant.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-[#00FF94] bg-[#00FF94]/10 border border-[#00FF94]/30 rounded-full px-3 py-1">
            <Shield className="w-3.5 h-3.5" /> CAN-SPAM / GDPR safe — outbound from your inbox, not from the platform
          </div>
        </header>

        {/* Add form */}
        <form onSubmit={createProspect} className="bg-neutral-900/50 border border-white/10 rounded-2xl p-5" data-testid="prospect-new-form">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#00F0FF]" /> Add a prospect
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-start">
            <div>
              <Label className="text-xs text-neutral-400">Company URL</Label>
              <Input
                type="text"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://company.ai"
                className="bg-black/40 border-white/10"
                data-testid="prospect-url-input"
              />
            </div>
            <div>
              <Label className="text-xs text-neutral-400">Internal note (optional)</Label>
              <Input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Why this prospect? Any intro angle?"
                className="bg-black/40 border-white/10"
                data-testid="prospect-note-input"
              />
            </div>
            <Button
              type="submit"
              disabled={creating || !newUrl.trim()}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold h-10 mt-5"
              data-testid="prospect-create-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add & enrich
            </Button>
          </div>
        </form>

        {/* Bulk importers — Apollo/Clay CSV + Product Hunt */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-5" data-testid="prospect-bulk-imports">
          <h2 className="text-lg font-bold mb-1 flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#00FF94]" /> Bulk import
          </h2>
          <p className="text-xs text-neutral-500 mb-4">
            Load hundreds of prospects in one go. No emails are scraped — you source them
            from compliant B2B providers or Product Hunt's public API, then reach out from
            your own inbox.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* CSV upload */}
            <div className="rounded-xl border border-white/10 bg-black/30 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Upload className="w-4 h-4 text-[#00F0FF]" />
                <div className="font-semibold text-sm">Apollo / Clay / Hunter CSV</div>
              </div>
              <p className="text-xs text-neutral-500 mb-3 leading-relaxed">
                Columns (any subset, case-insensitive):{' '}
                <code className="text-[#00F0FF] text-[10px]">url · company · first_name · last_name · email · title</code>
              </p>
              <label className="block">
                <input
                  type="file"
                  accept=".csv"
                  onChange={importCsv}
                  disabled={importingCsv}
                  hidden
                  data-testid="prospect-csv-input"
                />
                <Button
                  type="button"
                  disabled={importingCsv}
                  onClick={(ev) => ev.currentTarget.previousElementSibling?.click()}
                  className="bg-white/10 hover:bg-white/15 text-white border border-white/15 font-semibold h-9"
                  data-testid="prospect-csv-btn"
                >
                  {importingCsv ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload CSV
                </Button>
              </label>
            </div>

            {/* Product Hunt */}
            <div className="rounded-xl border border-white/10 bg-black/30 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-[#00FF94]" />
                <div className="font-semibold text-sm">Product Hunt — makers in a topic</div>
              </div>
              <p className="text-xs text-neutral-500 mb-3 leading-relaxed">
                Pulls product names, websites, maker handles for the top 30 posts in a
                topic. Emails never exposed — reach out via maker's public profile / site.
              </p>
              <div className="flex gap-2 items-center">
                <Input
                  type="text"
                  value={phTopic}
                  onChange={(e) => setPhTopic(e.target.value)}
                  placeholder="artificial-intelligence"
                  className="bg-black/40 border-white/10 h-9 text-xs flex-1"
                  data-testid="prospect-ph-topic-input"
                />
                <Button
                  type="button"
                  disabled={importingPh || !phTopic.trim()}
                  onClick={importFromProductHunt}
                  className="bg-[#00FF94] hover:bg-[#00FF94]/90 text-black font-semibold h-9"
                  data-testid="prospect-ph-btn"
                >
                  {importingPh ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pull makers'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Status tabs */}
        <div className="flex gap-2 overflow-x-auto pb-3" data-testid="prospect-status-tabs">
          {STATUS_ORDER.map((s) => {
            const meta = s === 'all'
              ? { label: 'All', color: '#ffffff', bg: 'bg-white/10', border: 'border-white/20' }
              : STATUS_META[s];
            const active = statusFilter === s;
            const count = counts[s] ?? 0;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap border transition-all ${
                  active
                    ? `${meta.bg} ${meta.border}`
                    : 'bg-neutral-900/50 border-white/10 hover:border-white/20'
                }`}
                style={{ color: active ? meta.color : '#cccccc' }}
                data-testid={`prospect-tab-${s}`}
              >
                {meta.label} <span className="opacity-70 ml-1">({count})</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
          </div>
        ) : prospects.length === 0 ? (
          <div className="text-center py-16 text-neutral-500" data-testid="prospect-empty">
            <Building2 className="w-12 h-12 text-neutral-700 mx-auto mb-3" />
            <p>No prospects in this tab yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="prospect-grid">
            {prospects.map((p) => (
              <ProspectCard
                key={p.prospect_id}
                prospect={p}
                onOpen={() => setSelected(p)}
                onReload={load}
              />
            ))}
          </div>
        )}

        {selected && (
          <ProspectDrawer
            prospect={selected}
            onClose={() => setSelected(null)}
            onReload={load}
          />
        )}
      </div>
    </AdminLayout>
  );
}


function ProspectCard({ prospect, onOpen, onReload }) {
  const meta = STATUS_META[prospect.status] || STATUS_META.new;

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete prospect for ${prospect.domain}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/prospects/${prospect.prospect_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Prospect removed');
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div
      className="bg-neutral-900/50 border border-white/10 hover:border-[#00F0FF]/40 rounded-2xl p-5 cursor-pointer transition-all"
      onClick={onOpen}
      data-testid={`prospect-card-${prospect.prospect_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          {prospect.logo ? (
            <img
              src={prospect.logo}
              alt={prospect.domain}
              className="w-10 h-10 rounded-lg object-cover border border-white/10 shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/15 border border-white/10 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-[#00F0FF]" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="font-bold text-white truncate">{prospect.title || prospect.domain}</h3>
            <p className="text-xs text-neutral-500 truncate">{prospect.domain}</p>
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="text-neutral-600 hover:text-red-400 transition-colors shrink-0"
          aria-label="Delete prospect"
          data-testid={`prospect-delete-${prospect.prospect_id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <p className="text-sm text-neutral-400 line-clamp-2 min-h-[2.5em] mb-3">
        {prospect.description || '—'}
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${meta.bg} ${meta.border} border`}
          style={{ color: meta.color }}
        >
          {meta.label}
        </span>
        {prospect.contact_email && (
          <span className="inline-flex items-center gap-1 text-[10px] text-neutral-400">
            <Mail className="w-3 h-3" /> {prospect.contact_email}
          </span>
        )}
        {Object.entries(prospect.socials || {}).map(([k, href]) => {
          const Icon = SOCIAL_ICON[k];
          if (!Icon) return null;
          return (
            <a
              key={k}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-neutral-500 hover:text-[#00F0FF]"
              aria-label={k}
            >
              <Icon className="w-3.5 h-3.5" />
            </a>
          );
        })}
      </div>
    </div>
  );
}


function ProspectDrawer({ prospect: initial, onClose, onReload }) {
  const [prospect, setProspect] = useState(initial);
  const contactName = prospect.contact_name || '';
  const companyName = prospect.title || prospect.domain;

  const [emailTo, setEmailTo] = useState(prospect.contact_email || (prospect.emails_found?.[0] || ''));
  const [emailSubject, setEmailSubject] = useState(DEFAULT_SUBJECT(companyName));
  const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_TEMPLATE(contactName, companyName));
  const [contactNameInput, setContactNameInput] = useState(prospect.contact_name || '');
  const [saving, setSaving] = useState(false);

  // Personalised partner PDF generator
  const [pdfProgram, setPdfProgram] = useState('featured_platform');
  const [pdfNote, setPdfNote] = useState('');
  const [pdfBuilding, setPdfBuilding] = useState(false);
  const [pdfEmailing, setPdfEmailing] = useState(false);

  const buildPersonalizedPdf = async () => {
    setPdfBuilding(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/prospects/${prospect.prospect_id}/personalized-pdf`,
        {
          recommended_program: pdfProgram,
          personal_note: pdfNote.trim() || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const slug = (prospect.title || prospect.domain || 'prospect')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `theailedger-${slug}-${pdfProgram}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Personalised PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to build PDF');
    } finally {
      setPdfBuilding(false);
    }
  };

  const emailPersonalizedPdf = async () => {
    const to = emailTo.trim();
    if (!to) {
      toast.error('Add a recipient email in the Compose section first');
      return;
    }
    setPdfEmailing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/prospects/${prospect.prospect_id}/email-personalized-pdf`,
        {
          recommended_program: pdfProgram,
          personal_note: pdfNote.trim() || null,
          to_email: to,
          // Reuse the Compose subject/body if the admin tweaked them;
          // backend falls back to a sensible default otherwise.
          subject: emailSubject?.trim() || null,
          email_body: pdfNote.trim() || emailBody?.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Sent to ${res.data.to_email} with PDF attached`);
      // Reflect status change (new → contacted) in the drawer + parent list
      setProspect({ ...prospect, status: prospect.status === 'new' ? 'contacted' : prospect.status, contacted_at: new Date().toISOString() });
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setPdfEmailing(false);
    }
  };

  const meta = STATUS_META[prospect.status] || STATUS_META.new;

  const openMailto = async () => {
    if (!emailTo.trim()) {
      toast.error('Add a recipient email first');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/prospects/${prospect.prospect_id}/mailto`,
        { to: emailTo.trim(), subject: emailSubject, body: emailBody },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Open the mailto: URL in the user's mail client
      window.location.href = res.data.mailto;
      toast.success(`Status → ${STATUS_META[res.data.status]?.label || res.data.status}`);
      onReload();
      setProspect({ ...prospect, status: res.data.status });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open mail client');
    }
  };

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        `${API_URL}/api/admin/prospects/${prospect.prospect_id}`,
        {
          status: newStatus,
          contact_email: emailTo.trim() || null,
          contact_name: contactNameInput.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProspect(res.data);
      toast.success('Updated');
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex" onClick={onClose}>
      <div
        className="ml-auto w-full md:w-[640px] lg:w-[760px] xl:w-[860px] h-full bg-neutral-950 border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="prospect-drawer"
      >
        <div className="sticky top-0 bg-neutral-950/95 backdrop-blur border-b border-white/10 p-5 flex items-start justify-between gap-4 z-10">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold break-words">{prospect.title || prospect.domain}</h2>
            <a
              href={prospect.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#00F0FF] hover:underline inline-flex items-center gap-1 break-all"
            >
              {prospect.url} <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-white shrink-0"
            data-testid="prospect-drawer-close"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-5">
          {prospect.description && (
            <p className="text-sm text-neutral-300">{prospect.description}</p>
          )}

          {/* Status + contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-neutral-400">Status</Label>
              <select
                value={prospect.status}
                onChange={(e) => updateStatus(e.target.value)}
                disabled={saving}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm mt-1"
                style={{ color: meta.color }}
                data-testid="prospect-status-select"
              >
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-neutral-400">Contact name</Label>
              <Input
                value={contactNameInput}
                onChange={(e) => setContactNameInput(e.target.value)}
                placeholder="e.g. Jordan"
                className="bg-black/40 border-white/10 mt-1"
                data-testid="prospect-contact-name"
              />
            </div>
          </div>

          {/* Socials & found emails */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-neutral-400">Socials found</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.keys(prospect.socials || {}).length === 0 && (
                  <span className="text-xs text-neutral-500">None detected</span>
                )}
                {Object.entries(prospect.socials || {}).map(([k, href]) => {
                  const Icon = SOCIAL_ICON[k] || ExternalLink;
                  return (
                    <a
                      key={k}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-neutral-800 text-xs text-neutral-300 hover:bg-neutral-700"
                    >
                      <Icon className="w-3.5 h-3.5" /> {k}
                    </a>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs text-neutral-400">Emails on page</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(prospect.emails_found || []).length === 0 && (
                  <span className="text-xs text-neutral-500">None visible</span>
                )}
                {(prospect.emails_found || []).map((em) => (
                  <button
                    key={em}
                    onClick={() => setEmailTo(em)}
                    className="text-xs text-[#00F0FF] hover:underline"
                  >
                    {em}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Compose */}
          <div className="bg-neutral-900/60 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-[#00F0FF]" />
              <span className="text-sm font-semibold">Compose outreach</span>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-neutral-400">To</Label>
                <Input
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="contact@company.ai"
                  className="bg-black/40 border-white/10 mt-1"
                  data-testid="prospect-email-to"
                />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Subject</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="bg-black/40 border-white/10 mt-1"
                  data-testid="prospect-email-subject"
                />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Body (personalise before sending)</Label>
                <Textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={12}
                  className="bg-black/40 border-white/10 mt-1 text-sm font-mono"
                  data-testid="prospect-email-body"
                />
              </div>
              <Button
                onClick={openMailto}
                disabled={!emailTo.trim()}
                className="w-full bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold"
                data-testid="prospect-send-btn"
              >
                <Mail className="w-4 h-4 mr-2" /> Open in mail client & mark contacted
              </Button>
              <p className="text-[11px] text-neutral-500 text-center">
                Opens your default mail app with fields pre-filled. You send from your own inbox.
              </p>
            </div>
          </div>

          {/* Personalised partner PDF */}
          <div className="bg-neutral-900/60 border border-white/10 rounded-xl p-4" data-testid="prospect-pdf-section">
            <div className="flex items-center gap-2 mb-3">
              <FileDown className="w-4 h-4 text-[#00F0FF]" />
              <span className="text-sm font-semibold">Personalised partner PDF</span>
            </div>
            <p className="text-[11px] text-neutral-500 mb-3">
              Generates a 2-page proposal tailored to <span className="text-neutral-300">{companyName}</span> with the recommended program highlighted. Attach to your outreach email.
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-neutral-400">Recommended program</Label>
                <select
                  value={pdfProgram}
                  onChange={(e) => setPdfProgram(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm mt-1"
                  data-testid="prospect-pdf-program"
                >
                  <option value="featured_platform">Featured Platform Partner ($99–$199/mo)</option>
                  <option value="founding_creator">Founding Creator (0% fee · first 100)</option>
                  <option value="standard_creator">Standard Creator (5% flat)</option>
                  <option value="build_partner">AI Build Partner (free listing · agreed rev share)</option>
                  <option value="affiliate">Affiliate Partner (free listing)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Personal note (optional, replaces the default opener)</Label>
                <Textarea
                  value={pdfNote}
                  onChange={(e) => setPdfNote(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  placeholder={`Hi ${contactName || 'there'}, we love what ${companyName} is building...`}
                  className="bg-black/40 border-white/10 mt-1 text-sm"
                  data-testid="prospect-pdf-note"
                />
                <div className="text-[10px] text-neutral-500 text-right mt-1">{pdfNote.length}/1000</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  onClick={buildPersonalizedPdf}
                  disabled={pdfBuilding || pdfEmailing}
                  className="w-full bg-white/10 hover:bg-white/15 border border-white/15 text-white"
                  data-testid="prospect-pdf-download-btn"
                >
                  {pdfBuilding ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</>
                  ) : (
                    <><FileDown className="w-4 h-4 mr-2" /> Download only</>
                  )}
                </Button>
                <Button
                  onClick={emailPersonalizedPdf}
                  disabled={pdfEmailing || pdfBuilding || !emailTo.trim()}
                  className="w-full bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold"
                  data-testid="prospect-pdf-email-btn"
                >
                  {pdfEmailing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" /> Send via Resend</>
                  )}
                </Button>
              </div>
              <p className="text-[11px] text-neutral-500 text-center">
                "Send via Resend" emails the PDF as an attachment to{' '}
                <span className="text-neutral-300">{emailTo || '(set a recipient in Compose first)'}</span>,
                logs the send, and marks the prospect as contacted.
              </p>
            </div>
          </div>

          {/* History */}
          {(prospect.history || []).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2 text-neutral-300">Activity</h3>
              <ul className="space-y-2">
                {prospect.history.slice().reverse().slice(0, 8).map((h, idx) => (
                  <li key={`${h.at}-${idx}`} className="text-xs text-neutral-500">
                    <span className="text-neutral-400">{new Date(h.at).toLocaleString()}</span>
                    {' — '}
                    {Object.entries(h.change || {}).map(([k, v]) => `${k}: ${String(v).slice(0, 60)}`).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
